// @flow

import * as React from 'react';
import Emojify from 'app/components/shared/Emojify';
import { shortCommit } from 'app/lib/commits';
import classNames from 'classnames';
import { jobTime } from 'app/lib/jobs';
import Duration from 'app/components/shared/Duration';
import Icon from 'app/components/shared/Icon';
import { elementIdFor } from 'app/components/job/Job';

export type TriggerStepType = {
  type: 'trigger',
  id: string,
  state: string,
  name: ?string,
  passed: ?boolean,
  triggerAsync: ?boolean,
  triggerBranch: string,
  triggerCommit: string,
  triggerCommitShortLength: number,
  triggerProjectSlug: ?string,
  triggerFailedReason: ?string,
  triggerProject?: {
    name: string
  },
  triggeredBuild?: {
    number: number,
    destroyedAt?: string,
    url: string
  },
  startedAt: ?string,
  finishedAt: ?string,
  canceledAt: ?string,
  timedOutAt: ?string,
  softFailed: ?boolean
};

type Props = {
  job: TriggerStepType
};

export default class TriggerJobSummary extends React.PureComponent<Props> {
  render() {
    const iconClassName = `mr2 build-details-pipeline-icon ${this.icon(this.props.job)}`;

    let state = (
      this.props.job.state === 'finished' ||
      this.props.job.state === 'timed_out' ||
      this.props.job.state === 'canceled'
        ? (
          this.props.job.passed
            ? 'passed'
            : 'failed'
        )
        : this.props.job.state
    );

    // Cancelling and timing out are still running, so let's show them as such.
    if (state === 'canceling' || state === 'timing_out') {
      state = 'running';
    }

    let contentsNode = (
      <div className="build-details-pipeline-job__header flex items-center p3 bg-white z2 rounded hover-bg-silver">
        <i className={iconClassName} />
        <div className="flex flex-auto items-center mr1">
          <span className="mr2">
            <Emojify text={this.props.job.name || "Trigger Build"} />
          </span>
          <span className="dark-gray">
            {"Triggered build of "}
            <code className="monospace">
              {this.props.job.triggerBranch}
            </code>
            {" \x2F "}
            <code className="monospace" title={this.props.job.triggerCommit}>
              {shortCommit(this.props.job.triggerCommit, this.props.job.triggerCommitShortLength)}
            </code>
            {" on "}
            {this.nameNode()}
          </span>
        </div>
        {this.durationNode()}
        {this.errorMessageNode()}
        {this.deletedNode()}
        <div className="flex-none dark-gray pl3">
          <Icon icon="chevron-right" style={{ height: 12, width: 12 }} />
        </div>
      </div>
    );

    const classes = classNames(
      "build-details-pipeline-job relative color-inherit focus-color-inherit text-decoration-none hover-color-inherit block",
      {
        [`build-details-pipeline-job-state-${state}`]: true
      }
    );

    if (this.props.job.triggeredBuild && !this.props.job.triggeredBuild.destroyedAt) {
      contentsNode = (
        <a href={this.props.job.triggeredBuild.url} className={classes} id={this.elementId()}>
          {contentsNode}
        </a>
      );
    } else {
      contentsNode = (
        <div className={classes} id={this.elementId()}>
          {contentsNode}
        </div>
      );
    }

    return contentsNode;
  }

  iconNode() {
    if (this.props.job.state === "finished") {
      if (this.props.job.passed) {
        return (
          <span style={{ color: "#69A76A" }}>
            <i className="fa fa-check" />
          </span>
        );
      }

      return (
        <span style={{ color: "#A94442" }}>
          ✖
        </span>
      );
    } else if (this.props.job.state === "running") {
      return (
        <span style={{ color: "#ebbf25" }}>
          <i className="fa fa-refresh fa-spin" />
        </span>
      );
    } else if (this.props.job.state === "canceling") {
      return (
        <span style={{ color: "#A94442" }}>
          <i className="fa fa-refresh fa-spin" />
        </span>
      );
    } else if (this.props.job.state.indexOf("failed") > 0) {
    // no-icon
    } else if (this.props.job.state === "canceled") {
      return (
        <span style={{ color: "#A94442" }}>
          ✖
        </span>
      );
    } else if (this.props.job.state === "skipped") {
      return (
        <span style={{ color: "#8eaeba" }}>
          <i className="fa fa fa-minus-square" />
        </span>
      );
    } else {
      return (
        <span>
          <i className="fa fa-clock-o dark-gray" />
        </span>
      );
    }
  }

  nameNode() {
    if (this.props.job.triggerProject) {
      return <Emojify text={this.props.job.triggerProject.name} />;
    }
    if (this.props.job.triggerProjectSlug) {
      return <code className="monospace">{this.props.job.triggerProjectSlug}</code>;
    }
    return "unknown pipeline";
  }

  durationNode() {
    const buildNumber = this.props.job.triggeredBuild
      ? `#${this.props.job.triggeredBuild.number}`
      : "";

    if (this.props.job.triggerAsync) {
      return (
        <div className="flex-none dark-gray pl2">
          Build {buildNumber} (asynchronous)
        </div>
      );
    }

    if (this.props.job.startedAt) {
      const runTense = (
        this.props.job.state === 'running'
          ? "running for"
          : "ran in"
      );

      const duration = jobTime({
        state: this.props.job.state.toUpperCase(),
        startedAt: this.props.job.startedAt,
        finishedAt: this.props.job.finishedAt,
        canceledAt: this.props.job.canceledAt,
        timedOutAt: this.props.job.timedOutAt
      });

      return (
        <div className="flex-none dark-gray pl2">
          <i className="fa fa-clock-o mr1" />
          <span>
            Build {buildNumber} {runTense} <Duration.Short {...duration} />
          </span>
        </div>
      );
    }
  }

  deletedNode() {
    return this.props.job.triggeredBuild && this.props.job.triggeredBuild.destroyedAt && (
      <div className="flex-none ml1 red">
        (Pipeline Deleted)
      </div>
    );
  }

  errorMessageNode() {
    return this.props.job.triggerFailedReason && (
      <div className="flex-none red max-width-3 ml1">
        <i className="fa fa-warning" /> {this.props.job.triggerFailedReason}
      </div>
    );
  }

  icon(job: TriggerStepType) {
    if (job.softFailed) {
      return "fa fa-exclamation-triangle";
    }

    switch (job.state) {
      case "pending": return "fa fa-clock-o";
      case "waiting": return "fa fa-clock-o";
      case "waiting_failed": return "fa fa-clock-o";
      case "blocked": return "fa fa-lock";
      case "blocked_failed": return "fa fa-lock";
      case "unblocked": return "fa fa-unlock";
      case "unblocked_failed": return "fa fa-unlock";
      case "limited": return "fa fa-clock-o";
      case "scheduled": return "fa fa-clock-o";
      case "assigned": return "fa fa-clock-o";
      case "accepted": return "fa fa-clock-o";
      case "skipped": return "fa fa-minus-square";
      case "running":
      case "canceling":
      case "timing_out":
        return "fa fa-refresh fa-spin";
      case "finished":
      case "timed_out":
      case "canceled":
        if (job.passed) {
          return "fa fa-check";
        }
        return "fa fa-times";
      default: return "";
    }
  }

  elementId() {
    return elementIdFor(this.props.job.id);
  }
}
